
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createAgent } from "@/services/admin/agentService";
import { fetchVoiceConfigs } from "@/services/admin/voiceConfigService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AgentFormData } from "@/types/admin";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().nullable(),
  voice_id: z.string().min(1, "Voice is required"),
  language: z.string().min(1, "Language is required"),
  dealership_id: z.string().nullable(),
  elevenlabs_agent_id: z.string().nullable(),
  first_message: z.string().optional().nullable(),
  max_duration_seconds: z.number().int().positive().optional().nullable(),
  goal: z.string().optional().nullable(),
  stability: z.number().min(0).max(1).optional().nullable(),
  speed: z.number().min(0.25).max(4).optional().nullable(),
  similarity_boost: z.number().min(0).max(1).optional().nullable(),
  optimize_latency: z.boolean().optional().nullable(),
});

interface AgentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AgentFormDialog = ({ open, onOpenChange, onSuccess }: AgentFormDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: null,
      voice_id: "",
      language: "en-US",
      dealership_id: null,
      elevenlabs_agent_id: null,
      first_message: null,
      max_duration_seconds: 120,
      goal: null,
      stability: 0.7,
      speed: 1.0,
      similarity_boost: 0.85,
      optimize_latency: true,
    },
  });

  const { data: voices, isLoading: isLoadingVoices } = useQuery({
    queryKey: ["voiceConfigs"],
    queryFn: fetchVoiceConfigs,
  });

  const createAgentMutation = useMutation({
    mutationFn: createAgent,
    onSuccess: () => {
      form.reset();
      onOpenChange(false);
      onSuccess();
    },
    onError: (error) => {
      console.error("Error creating agent:", error);
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    // Convert the form data to match AgentFormData requirements
    const agentData: AgentFormData = {
      name: data.name, // Required field
      description: data.description,
      voice_id: data.voice_id, // Required field
      language: data.language, // Required field
      dealership_id: data.dealership_id,
      elevenlabs_agent_id: data.elevenlabs_agent_id,
      first_message: data.first_message,
      max_duration_seconds: data.max_duration_seconds,
      goal: data.goal,
      stability: data.stability,
      speed: data.speed,
      similarity_boost: data.similarity_boost,
      optimize_latency: data.optimize_latency,
    };
    createAgentMutation.mutate(agentData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New AI Agent</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agent Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter agent name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe this agent's purpose" 
                      {...field} 
                      value={field.value || ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="voice_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Voice</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select voice" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoadingVoices ? (
                          <SelectItem value="loading" disabled>Loading voices...</SelectItem>
                        ) : voices && voices.length > 0 ? (
                          voices.map((voice) => (
                            <SelectItem key={voice.voice_id} value={voice.voice_id}>
                              {voice.name || voice.voice_id}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-voices" disabled>No voices available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Language</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="en-US">English (US)</SelectItem>
                        <SelectItem value="en-GB">English (UK)</SelectItem>
                        <SelectItem value="es-ES">Spanish</SelectItem>
                        <SelectItem value="fr-FR">French</SelectItem>
                        <SelectItem value="de-DE">German</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Agent"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
